import AppBox from "../../../../../commons/components/Box";
import {
  AppAvatarLabelSkeleton,
  AppBorderBox,
  AppButton,
  AppDivider,
  AppModal,
  AppPaper,
  AppText,
  AppTitle,
} from "../../../../../commons/components";
import TournamentManagerCard from "./TournamentManagerCard";
import { Fragment, useEffect, useState } from "react";
import {
  addRemoveTournamentManager,
  fetchTournamentManager,
} from "../../../../../redux/slices/football/admin/competition/manage-tournament-manager";
import { useAppDispatch, useAppSelector } from "../../../../../hooks/app";
import { useParams } from "react-router";
import { useSnackbar } from "../../../../../redux/snackbarProvider";
import ability, { Can } from "../../../../../casl/Can";
import ConfirmationModal from "../../../../../commons/components/ConfirmationModal";
import IconPersonAdd from "../../../../../commons/components/icons/person-add";
import { ResponseStatusCode } from "../../../../../commons/enums/response-status-code.enum";
import { RolePolicyActionEnum } from "../../../../../commons/enums/role-policy-action.enum";
import { StatusEnum } from "../../../../../commons/enums/status-enum";
import { FootballTournamentManager } from "../../../../../commons/models/football/football-tournament-manager";
import { User } from "../../../../../commons/models/user";
import UserSearch from "../../../users/components/UserSearch";
import { useTournamentManagerDialogInfo } from "../../hooks/useTournamentManagerDialogInfo";

export default function TournamentManager() {
  const { competitionId } = useParams();
  const dispatch = useAppDispatch();
  const snackBar = useSnackbar();

  const { managers, managerFetchStatus, addManagerStatus, removeManagerStatus } = useAppSelector(
    (state) => state.footballCompetitionManagers
  );
  const { competitionInfo } = useAppSelector((state) => state.footballManageCompetition);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [selectedManager, setSelectedManager] = useState<User | FootballTournamentManager>();
  const [confirmationDialogMode, setConfirmationDialogMode] = useState<"add" | "remove" | undefined>();

  const { dialogInfo, updateDialogInfo, resetDialogInfo } = useTournamentManagerDialogInfo();
  useEffect(() => {
    dispatch(fetchTournamentManager(Number(competitionId)));
  }, [competitionId]);

  const handleManagerSelect = (users: User[]) => {
    setIsAddDialogOpen(false);
    handleSelectedManagerAndOpenConfirmationDialog(users[0], "add");
  };
  const handleConfirmationAction = () => {
    setConfirmationDialogMode(undefined);
    if (selectedManager) {
      dispatch(
        addRemoveTournamentManager({
          tournamentId: Number(competitionId),
          userId: selectedManager.id,
          remove: confirmationDialogMode === "remove",
        })
      )
        .unwrap()
        .then(() => {
          setSelectedManager(undefined);
          dispatch(fetchTournamentManager(Number(competitionId)));
          const action = confirmationDialogMode === "remove" ? "Removed" : "Added";
          snackBar({
            key: `team-manager-${confirmationDialogMode}`,
            text: `Manager ${action} Successfully`,
            variant: "success",
          });
        })
        .catch((err) => {
          if (err.status !== ResponseStatusCode.EXCEPTION_FAILED) {
            snackBar({
              key: err.response.data.code,
              text: err.response.data.message,
              variant: "danger",
            });
          }
          resetDialogInfo();
        });
    }
  };

  const handleSelectedManagerAndOpenConfirmationDialog = (
    user: User | FootballTournamentManager,
    dialogMode: "add" | "remove"
  ) => {
    setSelectedManager(user);
    updateDialogInfo(user, dialogMode);
    setConfirmationDialogMode(dialogMode);
  };

  return (
    <AppBox flexDirection="column" gap="xs">
      <AppBox justifyContent={"space-between"} alignItems={"center"}>
        <AppTitle as="h5">Manager</AppTitle>
      </AppBox>
      <AppPaper padding={"none"} shadow={"xs"}>
        {managerFetchStatus === StatusEnum.Loading ? (
          <AppBox flexDirection="column">
            <AppAvatarLabelSkeleton />
          </AppBox>
        ) : managers.length === 0 ? (
          <AppPaper padding="sm" shadow="xs">
            <AppBorderBox borderVariant="dashed">
              <AppBox flexDirection="column" alignItems="center" gap="sm" justifyContent="center" p="sm">
                <Can I={RolePolicyActionEnum.CREATE} this={competitionInfo}>
                  <IconPersonAdd width={40} height={40} color="rgba(var(--border-200))" />
                </Can>
                <AppText fontWeight="semibold">There are not any manager in the tournament.</AppText>
                <Can I={RolePolicyActionEnum.CREATE} this={competitionInfo}>
                  <AppButton size="sm" label="Add Manager" onClick={() => setIsAddDialogOpen(true)} />
                </Can>
              </AppBox>
            </AppBorderBox>
          </AppPaper>
        ) : (
          <AppBox flexDirection="column">
            {managers.mapArray((manager, index) => (
              <Fragment key={manager.id}>
                <TournamentManagerCard
                  manager={manager}
                  withRemove={ability.can(RolePolicyActionEnum.CREATE, competitionInfo)}
                  onRemove={(user) => handleSelectedManagerAndOpenConfirmationDialog(user, "remove")}
                />
                {index !== managers.length - 1 && <AppDivider />}
              </Fragment>
            ))}
          </AppBox>
        )}
      </AppPaper>
      <AppModal
        opened={isAddDialogOpen}
        onClose={() => {
          setIsAddDialogOpen(false);
        }}
        titleIcon={<IconPersonAdd />}
        title={"Add Tournament Manager"}
        withoutButtons
      >
        <UserSearch userSelectedUsers={managers?.all() as User[]} onSelectedUsers={handleManagerSelect} />
      </AppModal>
      <ConfirmationModal
        isOpen={confirmationDialogMode !== undefined}
        icon={dialogInfo.icon}
        title={dialogInfo.title}
        message={dialogInfo.message}
        confirmButtonLabel={dialogInfo.buttonLabel}
        iconColor={dialogInfo.iconColor}
        onCancel={() => setConfirmationDialogMode(undefined)}
        onConfirm={handleConfirmationAction}
        withLoadingButton={removeManagerStatus === StatusEnum.Loading || addManagerStatus === StatusEnum.Loading}
      />
    </AppBox>
  );
}
